import {
  Plus,
  Eye,
  Check,
  X,
  TrendingUp,
  Upload,
  FileText,
  Download,
  ArrowUp,
  ArrowDown,
  Minus,
  RefreshCw,
  Edit,
} from 'lucide-react'
import { useState } from 'react'

import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { Checkbox } from '../components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog'
import { Input } from '../components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'

interface Supplier {
  id: number
  name: string
  contact: string
  phone: string
  email: string
  products: number
}

interface PurchaseOrder {
  id: string
  supplier: string
  date: string
  status: 'pending' | 'received' | 'partial'
  items: number
  total: number
}

interface ProductEntry {
  id: string
  xmlFileName: string
  supplier: string
  date: string
  entryDate: string
  paymentForm: string
  paymentMethod: string
  total: number
  items: number
  status: 'processed' | 'pending'
}

interface ProductFromXML {
  id: number
  name: string
  quantity: number
  newPrice: number
  previousPrice: number
  priceChange: number
  satKey: string
  lotNumber: string
  salePrice: number
  margin: number
  supplier: string
}

interface SuggestedProduct {
  id: number
  name: string
  currentStock: number
  avgSales: number
  daysRemaining: number
  suggested: number
  supplier: string
}

interface NewProductData {
  id: number
  name: string
  satKey: string
  category: string
  department: string
  productType: 'controlled' | 'antibiotic' | 'otc'
  lotNumber: string
  expiryDate: string
  unitType: string
  description: string
  isRegistered: boolean
}

interface PriceAdjustment {
  id: string
  supplier: string
  date: string
  purchaseId: string
  status: 'pending' | 'updated'
  items: number
  products: PriceAdjustmentProduct[]
  isEdited?: boolean
  editDate?: string
}

interface PriceAdjustmentProduct {
  id: number
  name: string
  quantity: number
  previousCost: number
  newCost: number
  costChange: number
  previousSalePrice: number
  previousMargin: number
  newSalePrice: number
  newMargin: number
  lotNumber: string
}

const suppliers: Supplier[] = [
  {
    id: 1,
    name: 'Laboratorios Pisa',
    contact: 'Juan Pérez',
    phone: '555-1234',
    email: 'ventas@pisa.com.mx',
    products: 45,
  },
  {
    id: 2,
    name: 'Farmacéutica GNP',
    contact: 'María González',
    phone: '555-5678',
    email: 'pedidos@gnp.com.mx',
    products: 38,
  },
  {
    id: 3,
    name: 'Distribuidora Farmacéutica',
    contact: 'Carlos Méndez',
    phone: '555-9012',
    email: 'contacto@distfarma.com',
    products: 52,
  },
]

const productEntries: ProductEntry[] = [
  {
    id: 'ING-001',
    xmlFileName: 'factura_pisa_202604_001.xml',
    supplier: 'Laboratorios Pisa',
    date: '2026-04-25',
    entryDate: '2026-04-26',
    paymentForm: 'Crédito',
    paymentMethod: 'Transferencia',
    total: 12500,
    items: 15,
    status: 'processed',
  },
  {
    id: 'ING-002',
    xmlFileName: 'factura_gnp_202604_045.xml',
    supplier: 'Farmacéutica GNP',
    date: '2026-04-24',
    entryDate: '2026-04-24',
    paymentForm: 'Contado',
    paymentMethod: 'Efectivo',
    total: 18900,
    items: 22,
    status: 'processed',
  },
]

const suggestedProducts: SuggestedProduct[] = [
  {
    id: 1,
    name: 'Ibuprofeno 400mg',
    currentStock: 15,
    avgSales: 25,
    daysRemaining: 0.6,
    suggested: 200,
    supplier: 'Farmacéutica GNP',
  },
  {
    id: 2,
    name: 'Losartán 50mg',
    currentStock: 8,
    avgSales: 18,
    daysRemaining: 0.4,
    suggested: 150,
    supplier: 'Laboratorios Pisa',
  },
  {
    id: 3,
    name: 'Paracetamol 500mg',
    currentStock: 50,
    avgSales: 30,
    daysRemaining: 1.6,
    suggested: 100,
    supplier: 'Laboratorios Pisa',
  },
]

const priceAdjustments: PriceAdjustment[] = [
  {
    id: 'ADJ-001',
    supplier: 'Laboratorios Pisa',
    date: '2026-05-10',
    purchaseId: 'ING-003',
    status: 'pending',
    items: 3,
    products: [
      {
        id: 1,
        name: 'Paracetamol 500mg',
        quantity: 100,
        previousCost: 45.0,
        newCost: 48.0,
        costChange: 6.67,
        previousSalePrice: 70.0,
        previousMargin: 35.56,
        newSalePrice: 75.0,
        newMargin: 35.42,
        lotNumber: 'LOT-2026-04-001',
      },
      {
        id: 2,
        name: 'Loratadina 10mg',
        quantity: 60,
        previousCost: 120.0,
        newCost: 130.0,
        costChange: 8.33,
        previousSalePrice: 180.0,
        previousMargin: 33.33,
        newSalePrice: 195.0,
        newMargin: 33.33,
        lotNumber: 'LOT-2026-04-004',
      },
      {
        id: 3,
        name: 'Omeprazol 20mg',
        quantity: 75,
        previousCost: 95.0,
        newCost: 92.0,
        costChange: -3.16,
        previousSalePrice: 145.0,
        previousMargin: 34.48,
        newSalePrice: 140.0,
        newMargin: 34.29,
        lotNumber: 'LOT-2026-04-005',
      },
    ],
  },
  {
    id: 'ADJ-002',
    supplier: 'Farmacéutica GNP',
    date: '2026-05-08',
    purchaseId: 'ING-002',
    status: 'updated',
    items: 2,
    isEdited: true,
    editDate: '2026-05-09',
    products: [
      {
        id: 4,
        name: 'Ibuprofeno 400mg',
        quantity: 80,
        previousCost: 65.0,
        newCost: 62.0,
        costChange: -4.62,
        previousSalePrice: 100.0,
        previousMargin: 35.0,
        newSalePrice: 95.0,
        newMargin: 34.74,
        lotNumber: 'LOT-2026-04-002',
      },
      {
        id: 5,
        name: 'Amoxicilina 500mg',
        quantity: 50,
        previousCost: 180.0,
        newCost: 180.0,
        costChange: 0,
        previousSalePrice: 270.0,
        previousMargin: 33.33,
        newSalePrice: 270.0,
        newMargin: 33.33,
        lotNumber: 'LOT-2026-04-003',
      },
    ],
  },
  {
    id: 'ADJ-003',
    supplier: 'Distribuidora Farmacéutica',
    date: '2026-05-05',
    purchaseId: 'ING-004',
    status: 'updated',
    items: 1,
    products: [
      {
        id: 6,
        name: 'Diclofenaco 100mg',
        quantity: 45,
        previousCost: 85.0,
        newCost: 88.0,
        costChange: 3.53,
        previousSalePrice: 130.0,
        previousMargin: 34.62,
        newSalePrice: 135.0,
        newMargin: 34.81,
        lotNumber: 'LOT-2026-04-006',
      },
    ],
  },
]

export function Purchases() {
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showManualEntryModal, setShowManualEntryModal] = useState(false)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [showReviewModal, setShowReviewModal] = useState(false)
  const [showAddSupplierModal, setShowAddSupplierModal] = useState(false)
  const [showPriceAdjustmentModal, setShowPriceAdjustmentModal] =
    useState(false)
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(
    null,
  )
  const [selectedEntry, setSelectedEntry] = useState<ProductEntry | null>(null)
  const [selectedAdjustment, setSelectedAdjustment] =
    useState<PriceAdjustment | null>(null)
  const [editableAdjustmentProducts, setEditableAdjustmentProducts] = useState<
    PriceAdjustmentProduct[]
  >([])
  const [keepPreviousPrice, setKeepPreviousPrice] = useState<{
    [key: number]: boolean
  }>({})
  const [adjustmentFilters, setAdjustmentFilters] = useState({
    supplier: '',
    status: '',
    dateFrom: '',
    dateTo: '',
  })
  const [suggestedFilters, setSuggestedFilters] = useState({
    dateFrom: '',
    dateTo: '',
  })
  const [xmlFile, setXmlFile] = useState<File | null>(null)
  const [catalogFile, setCatalogFile] = useState<File | null>(null)
  const [uploadData, setUploadData] = useState({
    paymentForm: '',
    paymentMethod: '',
    entryDate: '',
  })
  const [manualEntryData, setManualEntryData] = useState({
    noteNumber: '',
    supplier: '',
    purchaseDate: '',
    paymentForm: '',
    paymentMethod: '',
    entryDate: '',
  })
  const [newSupplier, setNewSupplier] = useState({
    name: '',
    contact: '',
    phone: '',
    email: '',
    website: '',
    catalogType: 'url' as 'url' | 'file',
  })
  const [extractedProducts, setExtractedProducts] = useState<ProductFromXML[]>(
    [],
  )
  const [newProductsData, setNewProductsData] = useState<NewProductData[]>([])
  const [manualNewProducts, setManualNewProducts] = useState<NewProductData[]>(
    [],
  )

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-700'
      case 'processed':
        return 'bg-green-100 text-green-700'
      default:
        return 'bg-slate-100 text-slate-700'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pendiente'
      case 'processed':
        return 'Procesado'
      default:
        return status
    }
  }

  const handleXmlUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file && file.name.endsWith('.xml')) {
      setXmlFile(file)
    }
  }

  const handleProcessXml = () => {
    // Simular extracción de productos del XML
    const mockProducts: ProductFromXML[] = [
      {
        id: 1,
        name: 'Paracetamol 500mg',
        quantity: 100,
        newPrice: 48.0,
        previousPrice: 45.0,
        priceChange: 6.67,
        satKey: '50161501',
        lotNumber: 'LOT-2026-04-001',
        salePrice: 75.0,
        margin: 35,
        supplier: 'Laboratorios Pisa',
      },
      {
        id: 2,
        name: 'Ibuprofeno 400mg',
        quantity: 80,
        newPrice: 62.0,
        previousPrice: 65.0,
        priceChange: -4.62,
        satKey: '50161502',
        lotNumber: 'LOT-2026-04-002',
        salePrice: 95.0,
        margin: 30,
        supplier: 'Laboratorios Pisa',
      },
      {
        id: 3,
        name: 'Amoxicilina 500mg',
        quantity: 50,
        newPrice: 180.0,
        previousPrice: 180.0,
        priceChange: 0,
        satKey: '51101506',
        lotNumber: 'LOT-2026-04-003',
        salePrice: 270.0,
        margin: 40,
        supplier: 'Laboratorios Pisa',
      },
      {
        id: 4,
        name: 'Loratadina 10mg',
        quantity: 60,
        newPrice: 130.0,
        previousPrice: 120.0,
        priceChange: 8.33,
        satKey: '51101801',
        lotNumber: 'LOT-2026-04-004',
        salePrice: 195.0,
        margin: 38,
        supplier: 'Laboratorios Pisa',
      },
      {
        id: 5,
        name: 'Omeprazol 20mg',
        quantity: 75,
        newPrice: 92.0,
        previousPrice: 95.0,
        priceChange: -3.16,
        satKey: '51171701',
        lotNumber: 'LOT-2026-04-005',
        salePrice: 140.0,
        margin: 32,
        supplier: 'Laboratorios Pisa',
      },
    ]

    setExtractedProducts(mockProducts)

    // Detectar productos no registrados (simulación - en producción verificaría contra inventario real)
    // Por ahora marcamos algunos productos como no registrados para demostración
    const unregisteredProducts: NewProductData[] = mockProducts
      .filter((p) => p.id === 4 || p.id === 5) // Simulamos que Loratadina y Omeprazol no están registrados
      .map((p) => ({
        id: p.id,
        name: p.name,
        satKey: p.satKey,
        category: '',
        department: '',
        productType: 'otc' as 'controlled' | 'antibiotic' | 'otc',
        lotNumber: p.lotNumber,
        expiryDate: '',
        unitType: 'piezas',
        description: '',
        isRegistered: false,
      }))

    setNewProductsData(unregisteredProducts)
    setShowUploadModal(false)
    setShowReviewModal(true)
  }

  const confirmProductEntry = () => {
    console.log('Confirmando ingreso de productos:', {
      file: xmlFile?.name,
      ...uploadData,
      products: extractedProducts,
      newProducts: newProductsData,
    })

    setShowReviewModal(false)
    setXmlFile(null)
    setUploadData({
      paymentForm: '',
      paymentMethod: '',
      entryDate: '',
    })
    setExtractedProducts([])
    setNewProductsData([])
  }

  const handleCatalogUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      setCatalogFile(file)
    }
  }

  const handleManualProductAdd = () => {
    // Simulación de agregar un producto manualmente
    // En producción, esto verificaría si el producto existe en inventario
    const newProduct: NewProductData = {
      id: Date.now(),
      name: 'Producto Nuevo',
      satKey: '',
      category: '',
      department: '',
      productType: 'otc',
      lotNumber: '',
      expiryDate: '',
      unitType: 'piezas',
      description: '',
      isRegistered: false,
    }
    setManualNewProducts([...manualNewProducts, newProduct])
  }

  const handleExcelUploadManual = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Simulación de procesamiento de Excel con productos no registrados
      const mockNewProducts: NewProductData[] = [
        {
          id: 1,
          name: 'Aspirina 500mg',
          satKey: '51121701',
          category: '',
          department: '',
          productType: 'otc',
          lotNumber: 'LOT-2026-05-001',
          expiryDate: '',
          unitType: 'piezas',
          description: '',
          isRegistered: false,
        },
        {
          id: 2,
          name: 'Diclofenaco Gel',
          satKey: '53131626',
          category: '',
          department: '',
          productType: 'otc',
          lotNumber: 'LOT-2026-05-002',
          expiryDate: '',
          unitType: 'tubos',
          description: '',
          isRegistered: false,
        },
      ]
      setManualNewProducts(mockNewProducts)
    }
  }

  const handleAddSupplier = () => {
    console.log('Agregando proveedor:', {
      ...newSupplier,
      catalogFile: catalogFile?.name,
    })

    setShowAddSupplierModal(false)
    setNewSupplier({
      name: '',
      contact: '',
      phone: '',
      email: '',
      website: '',
      catalogType: 'url',
    })
    setCatalogFile(null)
  }

  const openDetailModal = (entry: ProductEntry) => {
    setSelectedEntry(entry)
    setShowDetailModal(true)
  }

  const openPriceAdjustmentModal = (adjustment: PriceAdjustment) => {
    setSelectedAdjustment(adjustment)
    setEditableAdjustmentProducts([...adjustment.products])
    setKeepPreviousPrice({})
    setShowPriceAdjustmentModal(true)
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">
          Compras y Proveedores
        </h1>
      </div>

      <Tabs defaultValue="suggested" className="space-y-6">
        <TabsList>
          <TabsTrigger value="suggested">Pedidos Sugeridos</TabsTrigger>
          <TabsTrigger value="products">Productos Ingresados</TabsTrigger>
          <TabsTrigger value="adjustments">Ajustes de Precio</TabsTrigger>
          <TabsTrigger value="suppliers">Proveedores</TabsTrigger>
        </TabsList>

        {/* Suggested Orders */}
        <TabsContent value="suggested" className="space-y-4">
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
            <div className="flex items-start gap-3">
              <TrendingUp className="mt-0.5 h-5 w-5 text-blue-600" />
              <div>
                <h3 className="font-medium text-blue-900">
                  Pedidos basados en rotación
                </h3>
                <p className="mt-1 text-sm text-blue-700">
                  Estos productos requieren reabastecimiento según su velocidad
                  de venta
                </p>
              </div>
            </div>
          </div>

          {/* Filtros de Fecha */}
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <h3 className="mb-4 font-medium text-slate-900">Filtros</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Fecha Inicio
                </label>
                <Input
                  type="date"
                  value={suggestedFilters.dateFrom}
                  onChange={(e) =>
                    setSuggestedFilters({
                      ...suggestedFilters,
                      dateFrom: e.target.value,
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Fecha Fin
                </label>
                <Input
                  type="date"
                  value={suggestedFilters.dateTo}
                  onChange={(e) =>
                    setSuggestedFilters({
                      ...suggestedFilters,
                      dateTo: e.target.value,
                    })
                  }
                />
              </div>
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <table className="w-full">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Producto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Stock Actual
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Venta Promedio
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Días Rest.
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Cant. Sugerida
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Proveedor
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {suggestedProducts.map((product) => (
                  <tr key={product.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 font-medium text-slate-900">
                      {product.name}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`font-medium ${product.currentStock < 20 ? 'text-red-600' : 'text-slate-900'}`}
                      >
                        {product.currentStock}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-700">
                      {product.avgSales}/día
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`font-medium ${product.daysRemaining < 1 ? 'text-red-600' : 'text-slate-900'}`}
                      >
                        {product.daysRemaining.toFixed(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-semibold text-blue-600">
                      {product.suggested}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-700">
                      {product.supplier}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Product Entries */}
        <TabsContent value="products" className="space-y-4">
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm text-slate-600">
              {productEntries.length} ingresos registrados
            </p>
            <div className="flex gap-2">
              <Button
                onClick={() => setShowUploadModal(true)}
                variant="outline"
              >
                <Upload className="mr-2 h-4 w-4" />
                Cargar XML de Factura
              </Button>
              <Button onClick={() => setShowManualEntryModal(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Agregar sin XML
              </Button>
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <table className="w-full">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Folio
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Archivo XML
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Proveedor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Fecha Factura
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Fecha Ingreso
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Forma de Pago
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Total
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {productEntries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 font-medium text-slate-900">
                      {entry.id}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-blue-600" />
                        <span className="text-sm text-slate-700">
                          {entry.xmlFileName}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-700">
                      {entry.supplier}
                    </td>
                    <td className="px-6 py-4 text-slate-700">{entry.date}</td>
                    <td className="px-6 py-4 text-slate-700">
                      {entry.entryDate}
                    </td>
                    <td className="px-6 py-4">
                      <Badge
                        className={
                          entry.paymentForm === 'Contado'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-blue-100 text-blue-700'
                        }
                      >
                        {entry.paymentForm}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 font-semibold text-slate-900">
                      ${entry.total.toLocaleString()}
                    </td>
                    <td className="px-6 py-4">
                      <Badge className={getStatusColor(entry.status)}>
                        {getStatusLabel(entry.status)}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() => openDetailModal(entry)}
                          className="rounded p-1 hover:bg-slate-100"
                          title="Ver detalle"
                        >
                          <Eye className="h-4 w-4 text-slate-600" />
                        </button>
                        <button
                          className="rounded p-1 hover:bg-slate-100"
                          title="Descargar XML"
                        >
                          <Download className="h-4 w-4 text-blue-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Price Adjustments */}
        <TabsContent value="adjustments" className="space-y-4">
          {/* Filters */}
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <h3 className="mb-4 font-medium text-slate-900">Filtros</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Proveedor
                </label>
                <Select
                  value={adjustmentFilters.supplier}
                  onValueChange={(value) =>
                    setAdjustmentFilters({
                      ...adjustmentFilters,
                      supplier: value,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {suppliers.map((supplier) => (
                      <SelectItem key={supplier.id} value={supplier.name}>
                        {supplier.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Estado
                </label>
                <Select
                  value={adjustmentFilters.status}
                  onValueChange={(value) =>
                    setAdjustmentFilters({
                      ...adjustmentFilters,
                      status: value,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="pending">Pendiente</SelectItem>
                    <SelectItem value="updated">Actualizado</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Fecha Desde
                </label>
                <Input
                  type="date"
                  value={adjustmentFilters.dateFrom}
                  onChange={(e) =>
                    setAdjustmentFilters({
                      ...adjustmentFilters,
                      dateFrom: e.target.value,
                    })
                  }
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Fecha Hasta
                </label>
                <Input
                  type="date"
                  value={adjustmentFilters.dateTo}
                  onChange={(e) =>
                    setAdjustmentFilters({
                      ...adjustmentFilters,
                      dateTo: e.target.value,
                    })
                  }
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600">
              {
                priceAdjustments.filter((adj) => {
                  const matchSupplier =
                    !adjustmentFilters.supplier ||
                    adjustmentFilters.supplier === 'all' ||
                    adj.supplier === adjustmentFilters.supplier
                  const matchStatus =
                    !adjustmentFilters.status ||
                    adjustmentFilters.status === 'all' ||
                    adj.status === adjustmentFilters.status
                  const matchDateFrom =
                    !adjustmentFilters.dateFrom ||
                    adj.date >= adjustmentFilters.dateFrom
                  const matchDateTo =
                    !adjustmentFilters.dateTo ||
                    adj.date <= adjustmentFilters.dateTo
                  return (
                    matchSupplier && matchStatus && matchDateFrom && matchDateTo
                  )
                }).length
              }{' '}
              ajustes encontrados
            </p>
          </div>

          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <table className="w-full">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Proveedor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Fecha
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    ID Compra
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Productos
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Fecha Modificación
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {priceAdjustments
                  .filter((adj) => {
                    const matchSupplier =
                      !adjustmentFilters.supplier ||
                      adjustmentFilters.supplier === 'all' ||
                      adj.supplier === adjustmentFilters.supplier
                    const matchStatus =
                      !adjustmentFilters.status ||
                      adjustmentFilters.status === 'all' ||
                      adj.status === adjustmentFilters.status
                    const matchDateFrom =
                      !adjustmentFilters.dateFrom ||
                      adj.date >= adjustmentFilters.dateFrom
                    const matchDateTo =
                      !adjustmentFilters.dateTo ||
                      adj.date <= adjustmentFilters.dateTo
                    return (
                      matchSupplier &&
                      matchStatus &&
                      matchDateFrom &&
                      matchDateTo
                    )
                  })
                  .map((adjustment) => (
                    <tr key={adjustment.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 font-medium text-slate-900">
                        {adjustment.id}
                      </td>
                      <td className="px-6 py-4 text-slate-700">
                        {adjustment.supplier}
                      </td>
                      <td className="px-6 py-4 text-slate-700">
                        {adjustment.date}
                      </td>
                      <td className="px-6 py-4 text-slate-700">
                        {adjustment.purchaseId}
                      </td>
                      <td className="px-6 py-4 text-slate-700">
                        {adjustment.items} productos
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Badge
                            className={
                              adjustment.status === 'pending'
                                ? 'bg-yellow-100 text-yellow-700'
                                : 'bg-green-100 text-green-700'
                            }
                          >
                            {adjustment.status === 'pending'
                              ? 'Pendiente'
                              : 'Actualizado'}
                          </Badge>
                          {adjustment.isEdited && (
                            <Badge className="bg-purple-100 text-purple-700">
                              Editada
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-700">
                        {adjustment.editDate || '-'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => openPriceAdjustmentModal(adjustment)}
                            className="rounded p-1 hover:bg-slate-100"
                            title={
                              adjustment.status === 'pending'
                                ? 'Ver detalles'
                                : 'Ver/Editar'
                            }
                          >
                            <Eye className="h-4 w-4 text-slate-600" />
                          </button>
                          {adjustment.status === 'updated' && (
                            <button
                              onClick={() =>
                                openPriceAdjustmentModal(adjustment)
                              }
                              className="rounded p-1 hover:bg-slate-100"
                              title="Editar"
                            >
                              <Edit className="h-4 w-4 text-blue-600" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Suppliers */}
        <TabsContent value="suppliers" className="space-y-4">
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm text-slate-600">
              {suppliers.length} proveedores registrados
            </p>
            <Button onClick={() => setShowAddSupplierModal(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Agregar Proveedor
            </Button>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {suppliers.map((supplier) => (
              <div
                key={supplier.id}
                className="rounded-lg border border-slate-200 bg-white p-6"
              >
                <h3 className="mb-3 font-semibold text-slate-900">
                  {supplier.name}
                </h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-slate-600">Contacto:</span>
                    <p className="text-slate-900">{supplier.contact}</p>
                  </div>
                  <div>
                    <span className="text-slate-600">Teléfono:</span>
                    <p className="text-slate-900">{supplier.phone}</p>
                  </div>
                  <div>
                    <span className="text-slate-600">Email:</span>
                    <p className="text-slate-900">{supplier.email}</p>
                  </div>
                  <div>
                    <span className="text-slate-600">Productos:</span>
                    <p className="font-medium text-slate-900">
                      {supplier.products}
                    </p>
                  </div>
                </div>
                <Button className="mt-4 w-full" variant="outline" disabled>
                  Ver Catálogo
                </Button>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Upload XML Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Cargar Factura XML</DialogTitle>
            <DialogDescription className="sr-only">
              Cargar productos mediante archivo XML de factura
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {/* XML Upload */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Archivo XML de Factura <span className="text-red-600">*</span>
              </label>
              <div className="rounded-lg border-2 border-dashed border-slate-200 p-6 text-center transition-colors hover:border-blue-400">
                <input
                  type="file"
                  accept=".xml"
                  onChange={handleXmlUpload}
                  className="hidden"
                  id="xml-upload"
                />
                <label htmlFor="xml-upload" className="cursor-pointer">
                  {xmlFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <FileText className="h-8 w-8 text-blue-600" />
                      <div className="text-left">
                        <p className="text-sm font-medium text-slate-900">
                          {xmlFile.name}
                        </p>
                        <p className="text-xs text-slate-500">
                          {(xmlFile.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.preventDefault()
                          setXmlFile(null)
                        }}
                        className="rounded p-1 hover:bg-slate-100"
                      >
                        <X className="h-4 w-4 text-slate-600" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className="mx-auto mb-2 h-12 w-12 text-slate-400" />
                      <p className="text-sm text-slate-600">
                        Haz clic para seleccionar archivo XML
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        Formato CFDI 3.3 o 4.0
                      </p>
                    </>
                  )}
                </label>
              </div>
            </div>

            {/* Payment Form */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Forma de Pago <span className="text-red-600">*</span>
                </label>
                <Select
                  value={uploadData.paymentForm}
                  onValueChange={(value) =>
                    setUploadData({ ...uploadData, paymentForm: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar forma de pago" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contado">Contado</SelectItem>
                    <SelectItem value="credito">Crédito</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Método de Pago <span className="text-red-600">*</span>
                </label>
                <Select
                  value={uploadData.paymentMethod}
                  onValueChange={(value) =>
                    setUploadData({ ...uploadData, paymentMethod: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar método" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="efectivo">Efectivo</SelectItem>
                    <SelectItem value="transferencia">
                      Transferencia Bancaria
                    </SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="tarjeta">
                      Tarjeta de Crédito/Débito
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Entry Date */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Fecha de Ingreso <span className="text-red-600">*</span>
              </label>
              <Input
                type="date"
                value={new Date().toISOString().split('T')[0]}
                readOnly
                className="bg-slate-50"
              />
              <p className="text-xs text-slate-600">
                Fecha actual (no modificable)
              </p>
            </div>

            {/* Info Message */}
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <p className="mb-2 text-sm font-medium text-blue-900">
                Información:
              </p>
              <ul className="list-inside list-disc space-y-1 text-sm text-blue-800">
                <li>
                  El sistema procesará automáticamente los productos del XML
                </li>
                <li>Se validará que los productos existan en el catálogo</li>
                <li>
                  Se actualizará el inventario con las cantidades ingresadas
                </li>
                <li>
                  Se generará un registro de compra con todos los detalles
                </li>
              </ul>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                onClick={handleProcessXml}
                disabled={
                  !xmlFile ||
                  !uploadData.paymentForm ||
                  !uploadData.paymentMethod
                }
                className="flex-1"
              >
                Procesar Factura
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowUploadModal(false)
                  setXmlFile(null)
                  setUploadData({
                    paymentForm: '',
                    paymentMethod: '',
                    entryDate: '',
                  })
                }}
                className="flex-1"
              >
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalle de Ingreso</DialogTitle>
            <DialogDescription className="sr-only">
              Información detallada del ingreso de productos
            </DialogDescription>
          </DialogHeader>
          {selectedEntry && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 rounded-lg bg-slate-50 p-4">
                <div>
                  <p className="text-sm text-slate-600">Folio:</p>
                  <p className="font-semibold text-slate-900">
                    {selectedEntry.id}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Archivo XML:</p>
                  <p className="text-sm font-semibold text-slate-900">
                    {selectedEntry.xmlFileName}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Proveedor:</p>
                  <p className="font-semibold text-slate-900">
                    {selectedEntry.supplier}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Fecha Factura:</p>
                  <p className="font-semibold text-slate-900">
                    {selectedEntry.date}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Fecha Ingreso:</p>
                  <p className="font-semibold text-slate-900">
                    {selectedEntry.entryDate}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Forma de Pago:</p>
                  <p className="font-semibold text-slate-900">
                    {selectedEntry.paymentForm}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Método de Pago:</p>
                  <p className="font-semibold text-slate-900">
                    {selectedEntry.paymentMethod}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Total:</p>
                  <p className="font-semibold text-green-600">
                    ${selectedEntry.total.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Productos:</p>
                  <p className="font-semibold text-slate-900">
                    {selectedEntry.items} artículos
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Estado:</p>
                  <Badge className={getStatusColor(selectedEntry.status)}>
                    {getStatusLabel(selectedEntry.status)}
                  </Badge>
                </div>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" className="flex-1">
                  <Download className="mr-2 h-4 w-4" />
                  Descargar XML
                </Button>
                <Button variant="outline" className="flex-1">
                  <FileText className="mr-2 h-4 w-4" />
                  Ver Productos
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Supplier Modal */}
      <Dialog
        open={showAddSupplierModal}
        onOpenChange={setShowAddSupplierModal}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Agregar Nuevo Proveedor</DialogTitle>
            <DialogDescription className="sr-only">
              Agregar información de nuevo proveedor
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="font-medium text-slate-900">Información Básica</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Nombre del Proveedor <span className="text-red-600">*</span>
                  </label>
                  <Input
                    value={newSupplier.name}
                    onChange={(e) =>
                      setNewSupplier({ ...newSupplier, name: e.target.value })
                    }
                    placeholder="Ej: Laboratorios Pisa"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Nombre de Contacto <span className="text-red-600">*</span>
                  </label>
                  <Input
                    value={newSupplier.contact}
                    onChange={(e) =>
                      setNewSupplier({
                        ...newSupplier,
                        contact: e.target.value,
                      })
                    }
                    placeholder="Ej: Juan Pérez"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Teléfono <span className="text-red-600">*</span>
                  </label>
                  <Input
                    value={newSupplier.phone}
                    onChange={(e) =>
                      setNewSupplier({ ...newSupplier, phone: e.target.value })
                    }
                    placeholder="Ej: 555-1234"
                  />
                </div>

                <div className="col-span-2 space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Correo Electrónico <span className="text-red-600">*</span>
                  </label>
                  <Input
                    type="email"
                    value={newSupplier.email}
                    onChange={(e) =>
                      setNewSupplier({ ...newSupplier, email: e.target.value })
                    }
                    placeholder="Ej: ventas@proveedor.com"
                  />
                </div>
              </div>
            </div>

            {/* Catalog Information */}
            <div className="space-y-4">
              <h3 className="font-medium text-slate-900">
                Catálogo de Productos
              </h3>

              <div className="space-y-3">
                <label className="text-sm font-medium text-slate-700">
                  Tipo de Catálogo
                </label>
                <div className="flex gap-4">
                  <button
                    onClick={() =>
                      setNewSupplier({ ...newSupplier, catalogType: 'url' })
                    }
                    className={`flex-1 rounded-lg border-2 p-4 transition-all ${
                      newSupplier.catalogType === 'url'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <FileText className="mx-auto mb-2 h-6 w-6" />
                    <p className="text-sm font-medium">Página Web</p>
                  </button>
                  <button
                    onClick={() =>
                      setNewSupplier({ ...newSupplier, catalogType: 'file' })
                    }
                    className={`flex-1 rounded-lg border-2 p-4 transition-all ${
                      newSupplier.catalogType === 'file'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <Upload className="mx-auto mb-2 h-6 w-6" />
                    <p className="text-sm font-medium">Archivo Excel</p>
                  </button>
                </div>
              </div>

              {newSupplier.catalogType === 'url' ? (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    URL del Catálogo
                  </label>
                  <Input
                    value={newSupplier.website}
                    onChange={(e) =>
                      setNewSupplier({
                        ...newSupplier,
                        website: e.target.value,
                      })
                    }
                    placeholder="https://www.proveedor.com/catalogo"
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Archivo Excel del Catálogo
                  </label>
                  <div className="rounded-lg border-2 border-dashed border-slate-200 p-4 text-center transition-colors hover:border-blue-400">
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleCatalogUpload}
                      className="hidden"
                      id="catalog-upload"
                    />
                    <label htmlFor="catalog-upload" className="cursor-pointer">
                      {catalogFile ? (
                        <div className="flex items-center justify-center gap-2">
                          <FileText className="h-6 w-6 text-green-600" />
                          <div className="text-left">
                            <p className="text-sm font-medium text-slate-900">
                              {catalogFile.name}
                            </p>
                            <p className="text-xs text-slate-500">
                              {(catalogFile.size / 1024).toFixed(2)} KB
                            </p>
                          </div>
                          <button
                            onClick={(e) => {
                              e.preventDefault()
                              setCatalogFile(null)
                            }}
                            className="rounded p-1 hover:bg-slate-100"
                          >
                            <X className="h-4 w-4 text-slate-600" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <Upload className="mx-auto mb-2 h-10 w-10 text-slate-400" />
                          <p className="text-sm text-slate-600">
                            Haz clic para seleccionar archivo Excel
                          </p>
                          <p className="mt-1 text-xs text-slate-500">
                            Formatos: .xlsx, .xls
                          </p>
                        </>
                      )}
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Info Message */}
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <p className="mb-2 text-sm font-medium text-blue-900">
                Información:
              </p>
              <ul className="list-inside list-disc space-y-1 text-sm text-blue-800">
                <li>El catálogo de productos es opcional pero recomendado</li>
                <li>
                  Puedes proporcionar una URL web o subir un archivo Excel
                </li>
                <li>El catálogo facilitará la creación de órdenes de compra</li>
              </ul>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                onClick={handleAddSupplier}
                disabled={
                  !newSupplier.name ||
                  !newSupplier.contact ||
                  !newSupplier.phone ||
                  !newSupplier.email
                }
                className="flex-1"
                size="lg"
              >
                <Check className="mr-2 h-5 w-5" />
                Agregar Proveedor
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddSupplierModal(false)
                  setNewSupplier({
                    name: '',
                    contact: '',
                    phone: '',
                    email: '',
                    website: '',
                    catalogType: 'url',
                  })
                  setCatalogFile(null)
                }}
                className="flex-1"
                size="lg"
              >
                <X className="mr-2 h-5 w-5" />
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual Entry Modal */}
      <Dialog
        open={showManualEntryModal}
        onOpenChange={setShowManualEntryModal}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Agregar Productos sin XML</DialogTitle>
            <DialogDescription className="sr-only">
              Agregar productos manualmente sin archivo XML
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {/* Entry Information */}
            <div className="grid grid-cols-2 gap-4 rounded-lg bg-slate-50 p-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  No. Nota <span className="text-red-600">*</span>
                </label>
                <Input
                  value={manualEntryData.noteNumber}
                  onChange={(e) =>
                    setManualEntryData({
                      ...manualEntryData,
                      noteNumber: e.target.value,
                    })
                  }
                  placeholder="Ej: NOTA-001"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Proveedor <span className="text-red-600">*</span>
                </label>
                <Select
                  value={manualEntryData.supplier}
                  onValueChange={(value) =>
                    setManualEntryData({ ...manualEntryData, supplier: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar proveedor" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map((supplier) => (
                      <SelectItem key={supplier.id} value={supplier.name}>
                        {supplier.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Fecha de Compra <span className="text-red-600">*</span>
                </label>
                <Input
                  type="date"
                  value={manualEntryData.purchaseDate}
                  onChange={(e) =>
                    setManualEntryData({
                      ...manualEntryData,
                      purchaseDate: e.target.value,
                    })
                  }
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Fecha de Ingreso <span className="text-red-600">*</span>
                </label>
                <Input
                  type="date"
                  value={new Date().toISOString().split('T')[0]}
                  readOnly
                  className="bg-slate-50"
                />
                <p className="text-xs text-slate-600">
                  Fecha actual (no modificable)
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Forma de Pago <span className="text-red-600">*</span>
                </label>
                <Select
                  value={manualEntryData.paymentForm}
                  onValueChange={(value) =>
                    setManualEntryData({
                      ...manualEntryData,
                      paymentForm: value,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar forma de pago" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contado">Contado</SelectItem>
                    <SelectItem value="credito">Crédito</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Método de Pago <span className="text-red-600">*</span>
                </label>
                <Select
                  value={manualEntryData.paymentMethod}
                  onValueChange={(value) =>
                    setManualEntryData({
                      ...manualEntryData,
                      paymentMethod: value,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar método" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="efectivo">Efectivo</SelectItem>
                    <SelectItem value="transferencia">
                      Transferencia Bancaria
                    </SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="tarjeta">
                      Tarjeta de Crédito/Débito
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Products Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-slate-900">Productos</h3>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleManualProductAdd}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Agregar Producto
                  </Button>
                  <div className="relative">
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleExcelUploadManual}
                      className="hidden"
                      id="excel-upload"
                    />
                    <label htmlFor="excel-upload">
                      <Button size="sm" variant="outline" asChild>
                        <span className="cursor-pointer">
                          <Upload className="mr-2 h-4 w-4" />
                          Subir Excel
                        </span>
                      </Button>
                    </label>
                  </div>
                </div>
              </div>

              {manualNewProducts.length === 0 ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-center text-slate-500">
                  <p className="text-sm">No hay productos agregados</p>
                  <p className="mt-1 text-xs">
                    Agrega productos manualmente o sube un archivo Excel
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="rounded-lg border border-orange-200 bg-orange-50 p-3">
                    <p className="text-sm font-medium text-orange-900">
                      {manualNewProducts.length} producto
                      {manualNewProducts.length > 1 ? 's' : ''} detectado
                      {manualNewProducts.length > 1 ? 's' : ''}
                    </p>
                    <p className="mt-1 text-xs text-orange-800">
                      Completa la información para registrar los productos
                    </p>
                  </div>

                  {manualNewProducts.map((product, index) => (
                    <div
                      key={product.id}
                      className="rounded-lg border-2 border-orange-200 bg-white p-4"
                    >
                      <div className="mb-4 flex items-center justify-between">
                        <h5 className="flex items-center gap-2 font-medium text-slate-900">
                          <Badge
                            variant="outline"
                            className="border-orange-300 bg-orange-100 text-orange-700"
                          >
                            Nuevo
                          </Badge>
                          <Input
                            value={product.name}
                            onChange={(e) => {
                              const updated = [...manualNewProducts]
                              updated[index].name = e.target.value
                              setManualNewProducts(updated)
                            }}
                            placeholder="Nombre del producto"
                            className="flex-1"
                          />
                        </h5>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setManualNewProducts(
                              manualNewProducts.filter((_, i) => i !== index),
                            )
                          }}
                          className="text-red-600 hover:bg-red-50 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-slate-700">
                            Clave SAT <span className="text-red-600">*</span>
                          </label>
                          <Input
                            value={product.satKey}
                            onChange={(e) => {
                              const updated = [...manualNewProducts]
                              updated[index].satKey = e.target.value
                              setManualNewProducts(updated)
                            }}
                            placeholder="Ej: 51121701"
                          />
                        </div>

                        <div className="space-y-2">
                          <label className="text-sm font-medium text-slate-700">
                            Categoría <span className="text-red-600">*</span>
                          </label>
                          <Select
                            value={product.category}
                            onValueChange={(value) => {
                              const updated = [...manualNewProducts]
                              updated[index].category = value
                              setManualNewProducts(updated)
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Seleccionar" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="medicamentos">
                                Medicamentos
                              </SelectItem>
                              <SelectItem value="vitaminas">
                                Vitaminas y Suplementos
                              </SelectItem>
                              <SelectItem value="dermocosmetica">
                                Dermocosmética
                              </SelectItem>
                              <SelectItem value="higiene">
                                Higiene Personal
                              </SelectItem>
                              <SelectItem value="bebes">
                                Bebés y Maternidad
                              </SelectItem>
                              <SelectItem value="nutricion">
                                Nutrición
                              </SelectItem>
                              <SelectItem value="otros">Otros</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <label className="text-sm font-medium text-slate-700">
                            Departamento <span className="text-red-600">*</span>
                          </label>
                          <Select
                            value={product.department}
                            onValueChange={(value) => {
                              const updated = [...manualNewProducts]
                              updated[index].department = value
                              setManualNewProducts(updated)
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Seleccionar" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="antibioticos">
                                Antibióticos
                              </SelectItem>
                              <SelectItem value="antigripales">
                                Antigripales
                              </SelectItem>
                              <SelectItem value="analgesicos">
                                Analgésicos
                              </SelectItem>
                              <SelectItem value="cardiovascular">
                                Cardiovascular
                              </SelectItem>
                              <SelectItem value="gastrointestinal">
                                Gastrointestinal
                              </SelectItem>
                              <SelectItem value="dermatologia">
                                Dermatología
                              </SelectItem>
                              <SelectItem value="vitaminas">
                                Vitaminas y Suplementos
                              </SelectItem>
                              <SelectItem value="cuidado-personal">
                                Cuidado Personal
                              </SelectItem>
                              <SelectItem value="otros">Otros</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <label className="text-sm font-medium text-slate-700">
                            Tipo de Producto{' '}
                            <span className="text-red-600">*</span>
                          </label>
                          <Select
                            value={product.productType}
                            onValueChange={(
                              value: 'controlled' | 'antibiotic' | 'otc',
                            ) => {
                              const updated = [...manualNewProducts]
                              updated[index].productType = value
                              setManualNewProducts(updated)
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="otc">Venta Libre</SelectItem>
                              <SelectItem value="antibiotic">
                                Antibiótico
                              </SelectItem>
                              <SelectItem value="controlled">
                                Medicamento Controlado
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <label className="text-sm font-medium text-slate-700">
                            Número de Lote
                          </label>
                          <Input
                            value={product.lotNumber}
                            onChange={(e) => {
                              const updated = [...manualNewProducts]
                              updated[index].lotNumber = e.target.value
                              setManualNewProducts(updated)
                            }}
                            placeholder="LOT-2026-XX-XXX"
                          />
                        </div>

                        {(product.productType === 'antibiotic' ||
                          product.productType === 'controlled') && (
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-700">
                              Fecha de Caducidad{' '}
                              <span className="text-red-600">*</span>
                            </label>
                            <Input
                              type="date"
                              value={product.expiryDate}
                              onChange={(e) => {
                                const updated = [...manualNewProducts]
                                updated[index].expiryDate = e.target.value
                                setManualNewProducts(updated)
                              }}
                            />
                          </div>
                        )}

                        <div className="space-y-2">
                          <label className="text-sm font-medium text-slate-700">
                            Tipo de Unidad
                          </label>
                          <Select
                            value={product.unitType}
                            onValueChange={(value) => {
                              const updated = [...manualNewProducts]
                              updated[index].unitType = value
                              setManualNewProducts(updated)
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="piezas">Piezas</SelectItem>
                              <SelectItem value="cajas">Cajas</SelectItem>
                              <SelectItem value="frascos">Frascos</SelectItem>
                              <SelectItem value="sobres">Sobres</SelectItem>
                              <SelectItem value="tubos">Tubos</SelectItem>
                              <SelectItem value="ampolletas">
                                Ampolletas
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2 md:col-span-2">
                          <label className="text-sm font-medium text-slate-700">
                            Descripción
                          </label>
                          <Input
                            value={product.description}
                            onChange={(e) => {
                              const updated = [...manualNewProducts]
                              updated[index].description = e.target.value
                              setManualNewProducts(updated)
                            }}
                            placeholder="Descripción del producto"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Info Message */}
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <p className="mb-2 text-sm font-medium text-blue-900">
                Información:
              </p>
              <ul className="list-inside list-disc space-y-1 text-sm text-blue-800">
                <li>
                  Agrega productos manualmente cuando no tengas factura XML
                </li>
                <li>Puedes agregar múltiples productos a un mismo ingreso</li>
                <li>
                  Sube un archivo Excel con múltiples productos para carga
                  masiva
                </li>
                <li>Asegúrate de ingresar el número de nota para referencia</li>
                <li>
                  El inventario se actualizará automáticamente al confirmar
                </li>
              </ul>
            </div>

            {/* Excel Template Download */}
            <div className="rounded-lg border border-green-200 bg-green-50 p-4">
              <div className="flex items-start gap-3">
                <Download className="mt-0.5 h-5 w-5 text-green-600" />
                <div className="flex-1">
                  <h4 className="mb-1 font-medium text-green-900">
                    Plantilla de Excel
                  </h4>
                  <p className="mb-3 text-sm text-green-800">
                    Descarga la plantilla para cargar productos masivamente
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-green-300 hover:bg-green-100"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Descargar Plantilla Excel
                  </Button>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                disabled={
                  !manualEntryData.noteNumber ||
                  !manualEntryData.supplier ||
                  !manualEntryData.purchaseDate ||
                  !manualEntryData.paymentForm ||
                  !manualEntryData.paymentMethod ||
                  !manualEntryData.entryDate ||
                  manualNewProducts.some(
                    (p) =>
                      !p.name ||
                      !p.satKey ||
                      !p.category ||
                      !p.department ||
                      ((p.productType === 'antibiotic' ||
                        p.productType === 'controlled') &&
                        !p.expiryDate),
                  )
                }
                className="flex-1"
                size="lg"
              >
                <Check className="mr-2 h-5 w-5" />
                Confirmar Ingreso
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowManualEntryModal(false)
                  setManualEntryData({
                    noteNumber: '',
                    supplier: '',
                    purchaseDate: '',
                    paymentForm: '',
                    paymentMethod: '',
                    entryDate: '',
                  })
                  setManualNewProducts([])
                }}
                className="flex-1"
                size="lg"
              >
                <X className="mr-2 h-5 w-5" />
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Product Review Modal */}
      <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>
              Revisión de Productos - Comparación de Precios
            </DialogTitle>
            <DialogDescription className="sr-only">
              Revisar productos extraídos del XML y comparar cambios de precios
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {/* Summary */}
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <div className="flex items-start gap-3">
                <FileText className="mt-0.5 h-5 w-5 text-blue-600" />
                <div className="flex-1">
                  <h3 className="mb-1 font-medium text-blue-900">
                    {extractedProducts.length} productos encontrados en el XML
                  </h3>
                  {extractedProducts.length > 0 && (
                    <p className="mb-3 text-sm text-blue-800">
                      Proveedor:{' '}
                      <span className="font-semibold">
                        {extractedProducts[0].supplier}
                      </span>
                    </p>
                  )}
                  <div className="mt-3 grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-blue-700">Incrementos:</p>
                      <p className="font-semibold text-blue-900">
                        {
                          extractedProducts.filter((p) => p.priceChange > 0)
                            .length
                        }{' '}
                        productos
                      </p>
                    </div>
                    <div>
                      <p className="text-blue-700">Decrementos:</p>
                      <p className="font-semibold text-blue-900">
                        {
                          extractedProducts.filter((p) => p.priceChange < 0)
                            .length
                        }{' '}
                        productos
                      </p>
                    </div>
                    <div>
                      <p className="text-blue-700">Sin cambio:</p>
                      <p className="font-semibold text-blue-900">
                        {
                          extractedProducts.filter((p) => p.priceChange === 0)
                            .length
                        }{' '}
                        productos
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Products Table */}
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full">
                <thead className="border-b border-slate-200 bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                      Producto
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                      Cant.
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                      Precio Anterior
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                      Precio Nuevo
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                      Cambio
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                      Precio Venta
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                      Utilidad %
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                      Lote
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {extractedProducts.map((product) => (
                    <tr key={product.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm font-medium text-slate-900">
                            {product.name}
                          </p>
                          <p className="text-xs text-slate-500">
                            SAT: {product.satKey}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-slate-900">
                          {product.quantity}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-slate-700">
                          ${product.previousPrice.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-semibold text-slate-900">
                          ${product.newPrice.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {product.priceChange === 0 ? (
                          <div className="flex items-center gap-1 text-slate-600">
                            <Minus className="h-4 w-4" />
                            <span className="text-sm font-medium">
                              Sin cambio
                            </span>
                          </div>
                        ) : product.priceChange > 0 ? (
                          <div className="flex items-center gap-1 text-red-600">
                            <ArrowUp className="h-4 w-4" />
                            <span className="text-sm font-semibold">
                              +{product.priceChange.toFixed(2)}%
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-green-600">
                            <ArrowDown className="h-4 w-4" />
                            <span className="text-sm font-semibold">
                              {product.priceChange.toFixed(2)}%
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-slate-900">
                          ${product.salePrice.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-slate-900">
                          {product.margin.toFixed(2)}%
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-slate-600">
                          {product.lotNumber}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* New Products Registration */}
            {newProductsData.length > 0 && (
              <div className="space-y-4">
                <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
                  <div className="flex items-start gap-3">
                    <Plus className="mt-0.5 h-5 w-5 text-orange-600" />
                    <div>
                      <h4 className="font-medium text-orange-900">
                        {newProductsData.length} producto
                        {newProductsData.length > 1 ? 's' : ''} no registrado
                        {newProductsData.length > 1 ? 's' : ''}
                      </h4>
                      <p className="mt-1 text-sm text-orange-800">
                        Los siguientes productos no están en el inventario.
                        Completa la información para registrarlos.
                      </p>
                    </div>
                  </div>
                </div>

                {newProductsData.map((product, index) => (
                  <div
                    key={product.id}
                    className="rounded-lg border-2 border-orange-200 bg-orange-50/50 p-4"
                  >
                    <h5 className="mb-4 flex items-center gap-2 font-medium text-slate-900">
                      <Badge
                        variant="outline"
                        className="border-orange-300 bg-orange-100 text-orange-700"
                      >
                        Nuevo
                      </Badge>
                      {product.name}
                    </h5>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">
                          Categoría <span className="text-red-600">*</span>
                        </label>
                        <Select
                          value={product.category}
                          onValueChange={(value) => {
                            const updated = [...newProductsData]
                            updated[index].category = value
                            setNewProductsData(updated)
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccionar categoría" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="medicamentos">
                              Medicamentos
                            </SelectItem>
                            <SelectItem value="vitaminas">
                              Vitaminas y Suplementos
                            </SelectItem>
                            <SelectItem value="dermocosmetica">
                              Dermocosmética
                            </SelectItem>
                            <SelectItem value="higiene">
                              Higiene Personal
                            </SelectItem>
                            <SelectItem value="bebes">
                              Bebés y Maternidad
                            </SelectItem>
                            <SelectItem value="nutricion">Nutrición</SelectItem>
                            <SelectItem value="otros">Otros</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">
                          Departamento <span className="text-red-600">*</span>
                        </label>
                        <Select
                          value={product.department}
                          onValueChange={(value) => {
                            const updated = [...newProductsData]
                            updated[index].department = value
                            setNewProductsData(updated)
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccionar departamento" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="antibioticos">
                              Antibióticos
                            </SelectItem>
                            <SelectItem value="antigripales">
                              Antigripales
                            </SelectItem>
                            <SelectItem value="analgesicos">
                              Analgésicos
                            </SelectItem>
                            <SelectItem value="cardiovascular">
                              Cardiovascular
                            </SelectItem>
                            <SelectItem value="gastrointestinal">
                              Gastrointestinal
                            </SelectItem>
                            <SelectItem value="dermatologia">
                              Dermatología
                            </SelectItem>
                            <SelectItem value="vitaminas">
                              Vitaminas y Suplementos
                            </SelectItem>
                            <SelectItem value="cuidado-personal">
                              Cuidado Personal
                            </SelectItem>
                            <SelectItem value="otros">Otros</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">
                          Tipo de Producto{' '}
                          <span className="text-red-600">*</span>
                        </label>
                        <Select
                          value={product.productType}
                          onValueChange={(
                            value: 'controlled' | 'antibiotic' | 'otc',
                          ) => {
                            const updated = [...newProductsData]
                            updated[index].productType = value
                            setNewProductsData(updated)
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="otc">Venta Libre</SelectItem>
                            <SelectItem value="antibiotic">
                              Antibiótico
                            </SelectItem>
                            <SelectItem value="controlled">
                              Medicamento Controlado
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {(product.productType === 'antibiotic' ||
                        product.productType === 'controlled') && (
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-slate-700">
                            Fecha de Caducidad{' '}
                            <span className="text-red-600">*</span>
                          </label>
                          <Input
                            type="date"
                            value={product.expiryDate}
                            onChange={(e) => {
                              const updated = [...newProductsData]
                              updated[index].expiryDate = e.target.value
                              setNewProductsData(updated)
                            }}
                          />
                        </div>
                      )}

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">
                          Tipo de Unidad
                        </label>
                        <Select
                          value={product.unitType}
                          onValueChange={(value) => {
                            const updated = [...newProductsData]
                            updated[index].unitType = value
                            setNewProductsData(updated)
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="piezas">Piezas</SelectItem>
                            <SelectItem value="cajas">Cajas</SelectItem>
                            <SelectItem value="frascos">Frascos</SelectItem>
                            <SelectItem value="sobres">Sobres</SelectItem>
                            <SelectItem value="tubos">Tubos</SelectItem>
                            <SelectItem value="ampolletas">
                              Ampolletas
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2 md:col-span-2">
                        <label className="text-sm font-medium text-slate-700">
                          Descripción
                        </label>
                        <Input
                          value={product.description}
                          onChange={(e) => {
                            const updated = [...newProductsData]
                            updated[index].description = e.target.value
                            setNewProductsData(updated)
                          }}
                          placeholder="Descripción del producto"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Price Changes Alert */}
            {extractedProducts.some((p) => Math.abs(p.priceChange) > 10) && (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
                <div className="flex items-start gap-3">
                  <TrendingUp className="mt-0.5 h-5 w-5 text-yellow-600" />
                  <div>
                    <h4 className="font-medium text-yellow-900">
                      Cambios significativos detectados
                    </h4>
                    <p className="mt-1 text-sm text-yellow-800">
                      Algunos productos tienen cambios de precio superiores al
                      10%. Revisa los precios antes de confirmar.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                onClick={confirmProductEntry}
                disabled={newProductsData.some(
                  (p) =>
                    !p.category ||
                    !p.department ||
                    ((p.productType === 'antibiotic' ||
                      p.productType === 'controlled') &&
                      !p.expiryDate),
                )}
                className="flex-1"
                size="lg"
              >
                <Check className="mr-2 h-5 w-5" />
                Confirmar Ingreso de Productos
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowReviewModal(false)
                  setShowUploadModal(true)
                  setNewProductsData([])
                }}
                className="flex-1"
                size="lg"
              >
                <X className="mr-2 h-5 w-5" />
                Cancelar y Volver
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Price Adjustment Detail Modal */}
      <Dialog
        open={showPriceAdjustmentModal}
        onOpenChange={setShowPriceAdjustmentModal}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-6xl">
          <DialogHeader>
            <DialogTitle>Detalle de Ajuste de Precio</DialogTitle>
            <DialogDescription className="sr-only">
              Detalles de los productos con cambios de precio
            </DialogDescription>
          </DialogHeader>
          {selectedAdjustment && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-4 gap-4 rounded-lg bg-slate-50 p-4">
                <div>
                  <p className="text-sm text-slate-600">ID:</p>
                  <p className="font-semibold text-slate-900">
                    {selectedAdjustment.id}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Proveedor:</p>
                  <p className="font-semibold text-slate-900">
                    {selectedAdjustment.supplier}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Fecha:</p>
                  <p className="font-semibold text-slate-900">
                    {selectedAdjustment.date}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">ID Compra:</p>
                  <p className="font-semibold text-slate-900">
                    {selectedAdjustment.purchaseId}
                  </p>
                </div>
              </div>

              {/* Products Table */}
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-200 bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Mantener Anterior
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Producto
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Cant.
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Costo Anterior
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Costo Nuevo
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Cambio
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Precio Venta Anterior
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Utilidad % Anterior
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Precio Venta Nuevo
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Utilidad % Nuevo
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Lote
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {editableAdjustmentProducts.map((product, index) => {
                      const keepPrevious =
                        keepPreviousPrice[product.id] || false
                      const displaySalePrice = keepPrevious
                        ? product.previousSalePrice
                        : product.newSalePrice
                      const displayMargin = keepPrevious
                        ? product.previousMargin
                        : product.newMargin

                      return (
                        <tr key={product.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center">
                              <Checkbox
                                checked={keepPrevious}
                                onCheckedChange={(checked) => {
                                  setKeepPreviousPrice({
                                    ...keepPreviousPrice,
                                    [product.id]: checked as boolean,
                                  })
                                }}
                              />
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-medium text-slate-900">
                              {product.name}
                            </p>
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-medium text-slate-900">
                              {product.quantity}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-slate-700">
                              ${product.previousCost.toFixed(2)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-semibold text-slate-900">
                              ${product.newCost.toFixed(2)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {product.costChange === 0 ? (
                              <div className="flex items-center gap-1 text-slate-600">
                                <Minus className="h-4 w-4" />
                                <span className="text-sm font-medium">0%</span>
                              </div>
                            ) : product.costChange > 0 ? (
                              <div className="flex items-center gap-1 text-red-600">
                                <ArrowUp className="h-4 w-4" />
                                <span className="text-sm font-semibold">
                                  +{product.costChange.toFixed(2)}%
                                </span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 text-green-600">
                                <ArrowDown className="h-4 w-4" />
                                <span className="text-sm font-semibold">
                                  {product.costChange.toFixed(2)}%
                                </span>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-slate-700">
                              ${product.previousSalePrice.toFixed(2)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-slate-700">
                              {product.previousMargin.toFixed(2)}%
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <Input
                              type="number"
                              step="0.01"
                              value={displaySalePrice.toFixed(2)}
                              onChange={(e) => {
                                const newPrice = parseFloat(e.target.value) || 0
                                const newMargin =
                                  ((newPrice - product.newCost) /
                                    product.newCost) *
                                  100
                                const updated = [...editableAdjustmentProducts]
                                updated[index] = {
                                  ...product,
                                  newSalePrice: newPrice,
                                  newMargin: newMargin,
                                }
                                setEditableAdjustmentProducts(updated)
                              }}
                              disabled={keepPrevious}
                              className="h-8 w-24 text-sm"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <Input
                              type="number"
                              step="0.01"
                              value={displayMargin.toFixed(2)}
                              onChange={(e) => {
                                const newMargin =
                                  parseFloat(e.target.value) || 0
                                const newPrice =
                                  product.newCost * (1 + newMargin / 100)
                                const updated = [...editableAdjustmentProducts]
                                updated[index] = {
                                  ...product,
                                  newSalePrice: newPrice,
                                  newMargin: newMargin,
                                }
                                setEditableAdjustmentProducts(updated)
                              }}
                              disabled={keepPrevious}
                              className="h-8 w-20 text-sm"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs text-slate-600">
                              {product.lotNumber}
                            </span>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    console.log('Aplicando ajustes de precio:', {
                      adjustment: selectedAdjustment,
                      updatedProducts: editableAdjustmentProducts,
                      keepPrevious: keepPreviousPrice,
                    })
                    setShowPriceAdjustmentModal(false)
                  }}
                  className="flex-1"
                  size="lg"
                >
                  <RefreshCw className="mr-2 h-5 w-5" />
                  Aplicar Ajustes de Precio
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowPriceAdjustmentModal(false)}
                  className="flex-1"
                  size="lg"
                >
                  <X className="mr-2 h-5 w-5" />
                  Cerrar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
